import { type ReactElement } from 'react'
import { confirmDelete, useRequiredParams } from '@util'
import { isDeleted, type RubricData, type RubricId, type UnsavedRubric } from '@terros/common'
import { useRouter, createFileRoute } from '@tanstack/react-router'
import { notifications, showNotification } from '@mantine/notifications'
import { useToastError } from '@hooks'
import { useRubric, useUpdateRubric, useRemoveRubric, useTranslation, useProfile } from '@front/model'
import { ButtonDelete, ButtonSave, DangerZone, NotReady, Page, PermissionTooltip, TButton } from '@components'
import {
  ScorecardFormContext,
  PitchScorecardForm,
  usePitchScorecardForm,
  usePitchScorecardFormContextState,
} from '../settings.scorecard_.add/exports'

function PitchScorecardEditPage(): ReactElement {
  const { t } = useTranslation()
  const { rubricId } = useRequiredParams<{ rubricId: RubricId }>()
  const { data: rubric, isLoading, error } = useRubric(rubricId)

  if (isLoading || error || !rubric) {
    return <NotReady data={rubric} error={error} id={t('Conversation Scorecard')} isLoading={isLoading} />
  }

  return <RubricEditWithData rubric={rubric} rubricId={rubricId} />
}

const RubricEditWithData = ({ rubric, rubricId }: { rubric: RubricData; rubricId: RubricId }): ReactElement => {
  const { company } = useProfile()
  const { t } = useTranslation()
  const { update, isUpdating, error: updateError } = useUpdateRubric()
  const { remove, isRemoving, error: removeError } = useRemoveRubric()
  const router = useRouter()
  const scopeId = rubric.scopeId ?? company?.companyId
  useToastError(updateError)
  useToastError(removeError)

  const form = usePitchScorecardForm(rubric, t)
  const contextState = usePitchScorecardFormContextState(form)
  const contextValue = { form, ...contextState }

  const handleSubmit = async (unsaved: UnsavedRubric): Promise<void> => {
    await update({ ...unsaved, rubricId })
    router.history.back()
    form.resetDirty(unsaved)
    notifications.show({ message: t('Conversation scorecard saved'), color: 'green' })
  }

  const handleMoveToTrash = async (): Promise<void> => {
    const confirmed = await confirmDelete({
      title: t('Move Conversation Scorecard to Trash'),
      message: t('Are you sure you want to move this conversation scorecard to trash?'),
      label: t('Move to Trash'),
      t,
    })
    if (!confirmed) return

    await remove({ rubricId, archive: true })
    showNotification({
      title: t('Conversation Scorecard Moved to Trash'),
      message: t('You can restore {{title}} anytime from the Trash.', { title: rubric.title }),
      color: 'green',
    })
    form.setFieldValue('isDeleted', true)
    form.resetDirty()
  }

  const handleRestore = async (): Promise<void> => {
    await update({ ...rubric, isDeleted: false })
    form.setFieldValue('isDeleted', false)
    form.resetDirty()
    notifications.show({ message: t('Conversation scorecard restored'), color: 'green' })
  }

  return (
    <ScorecardFormContext.Provider value={contextValue}>
      <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
        <Page>
          <Page.Heading back title={t('Edit Conversation Scorecard')}>
            <PermissionTooltip permissionType='rubric:manage' targetId={scopeId}>
              <ButtonSave disabled={!form.isDirty() || !form.isValid()} loading={isUpdating} type='submit' />
            </PermissionTooltip>
          </Page.Heading>
          <Page.Content scrollArea>
            <PitchScorecardForm />
            {isDeleted(rubric) ? (
              <PermissionTooltip permissionType='rubric:manage' targetId={scopeId}>
                <TButton variant='secondary' onClick={handleRestore}>
                  {t('Restore from Trash')}
                </TButton>
              </PermissionTooltip>
            ) : (
              <DangerZone>
                <PermissionTooltip permissionType='rubric:manage' targetId={scopeId}>
                  <ButtonDelete loading={isRemoving} onClick={handleMoveToTrash}>
                    {t('Move Conversation Scorecard to Trash')}
                  </ButtonDelete>
                </PermissionTooltip>
              </DangerZone>
            )}
          </Page.Content>
        </Page>
      </form>
    </ScorecardFormContext.Provider>
  )
}

export const Route = createFileRoute('/settings/scorecard_/$rubricId/')({
  component: PitchScorecardEditPage,
})
